<template>
  <div class="scenario-list">
    <div class="panel-heading">
      <div class="panel-heading-left">
        <button-tooltip
          v-if="canEdit"
          :tooltip="tooltipMessage"
          :disabled="isCreateDisabled"
          :tooltip-type="createTooltipType"
          position="is-bottom"
          type="is-primary"
          icon-left="plus"
          size="is-small"
          @click="$emit('create')"
        />
      </div>

      <div class="panel-heading-right">
        <b-input
          v-model="searchQuery"
          placeholder="Search examples..."
          size="is-small"
          icon="search"
          class="mr-2"
          icon-right="close-circle"
          icon-right-clickable
          @icon-right-click="clearSearch"
        />

        <StatusFilter
          :scenarios="scenarios"
          :selected-statuses="selectedStatuses"
          @update-statuses="selectedStatuses = $event"
        />

        <ScenarioTypeFilter
          :scenarios="scenarios"
          :active-categories="activeCategories"
          :filtered-scenarios="filteredScenarios"
          @toggle-filter="toggleFilter"
        />
      </div>
    </div>

    <div class="scenarios-container">
      <div v-if="filteredScenarios.length === 0" class="has-text-centered p-4">
        <p v-if="!scenarios || scenarios.length === 0">
          No examples available for this topic
        </p>
        <p v-else-if="filteredScenarios.length === 0">
          No examples match your filter
          <span v-if="searchQuery">"{{ searchQuery }}"</span>
        </p>
      </div>

      <div v-else>
        <ScenarioCard
          v-for="scenario in filteredScenarios"
          :key="scenario.id"
          :scenario="scenario"
          :is-selected="scenario.id === selectedScenarioId"
          :is-draggable="enableDragDrop"
          @select="$emit('select', scenario.id)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ScenarioCard from "../molecules/ScenarioCard.vue";
import ButtonTooltip from "@/components/ui/ButtonTooltip.vue";
import ScenarioTypeFilter from "../molecules/ScenarioTypeFilter.vue";
import StatusFilter from "../molecules/StatusFilter.vue";
import { ContentStatus } from "vue-chess-shared";
import Fuse from "fuse.js";

export default {
  name: "ScenarioList",

  components: {
    ScenarioCard,
    ButtonTooltip,
    ScenarioTypeFilter,
    StatusFilter,
  },

  props: {
    scenarios: {
      type: Array,
      default: () => [],
    },
    selectedScenarioId: {
      type: String,
      default: null,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    enableDragDrop: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      activeCategories: ["examples", "puzzles", "classwork", "homework"],
      selectedStatuses: [ContentStatus.Draft, ContentStatus.Published], // default value
      searchQuery: "",
      debouncedSearchQuery: "",
      searchDebounceTimeout: null,
      ContentStatus,
      fuseOptions: {
        // Fuse.js options
        keys: ["title", "description", "tags", "status", "pgn"],
        includeScore: true,
        threshold: 0.2, // Lower threshold means more strict matching (0.0 is exact match)
        minMatchCharLength: 2,
        shouldSort: true,
        ignoreLocation: true,
        useExtendedSearch: true,
      },
    };
  },

  watch: {
    searchQuery: {
      handler(newValue) {
        // Clear any existing timeout
        if (this.searchDebounceTimeout) {
          clearTimeout(this.searchDebounceTimeout);
        }

        // Set a new timeout to update the debounced value
        this.searchDebounceTimeout = setTimeout(() => {
          this.debouncedSearchQuery = newValue;
        }, 300); // 300ms debounce time
      },
      immediate: true,
    },
  },

  computed: {
    fuse() {
      // Create a new Fuse instance with the current scenarios
      return new Fuse(this.scenarios, this.fuseOptions);
    },

    filteredScenarios() {
      let filtered = [...this.scenarios];

      // Add fuzzy search filtering
      if (this.debouncedSearchQuery) {
        // Use Fuse.js for fuzzy search
        const searchResults = this.fuse.search(this.debouncedSearchQuery);
        filtered = searchResults.map((result) => result.item);
      }

      // Add status filtering
      if (this.selectedStatuses.length > 0) {
        filtered = filtered.filter((scenario) =>
          this.selectedStatuses.includes(scenario.status)
        );
      }

      // Existing category filtering
      if (this.activeCategories.length > 0) {
        filtered = filtered.filter((scenario) => {
          if (
            !this.activeCategories.includes("examples") &&
            !scenario.isPuzzle
          ) {
            return false;
          }
          if (!this.activeCategories.includes("puzzles") && scenario.isPuzzle) {
            return false;
          }
          if (
            !this.activeCategories.includes("classwork") &&
            !scenario.isHomework
          ) {
            return false;
          }
          if (
            !this.activeCategories.includes("homework") &&
            scenario.isHomework
          ) {
            return false;
          }
          return true;
        });
      }

      return filtered;
    },

    isCreateDisabled() {
      // Count active scenarios (Draft + Published)
      const activeCount = this.scenarios.filter(
        (s) =>
          s.status === ContentStatus.Draft ||
          s.status === ContentStatus.Published
      ).length;
      return activeCount >= 60;
    },

    createTooltipType() {
      return this.isCreateDisabled ? "is-danger" : "is-primary";
    },

    tooltipMessage() {
      if (this.isCreateDisabled) {
        // Count active scenarios (Draft + Published)
        const activeCount = this.scenarios.filter(
          (s) =>
            s.status === ContentStatus.Draft ||
            s.status === ContentStatus.Published
        ).length;
        return `Exceeded limit of 60 active examples (current: ${activeCount})`;
      }
      return "Create Example";
    },
  },

  methods: {
    toggleFilter(filterType) {
      const index = this.activeCategories.indexOf(filterType);

      if (index === -1) {
        this.activeCategories.push(filterType);
      } else {
        this.activeCategories.splice(index, 1);

        // Define filter pairs to ensure at least one in each pair is active
        const filterPairs = {
          examples: "puzzles",
          puzzles: "examples",
          classwork: "homework",
          homework: "classwork",
        };
        const complementaryFilter = filterPairs[filterType];
        // If the complementary filter is not active, add it
        if (!this.activeCategories.includes(complementaryFilter)) {
          this.activeCategories.push(complementaryFilter);
        }
      }
    },

    clearSearch() {
      this.searchQuery = "";
      this.debouncedSearchQuery = "";
      if (this.searchDebounceTimeout) {
        clearTimeout(this.searchDebounceTimeout);
        this.searchDebounceTimeout = null;
      }
    },
  },
};
</script>

<style scoped>
.scenario-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  border: 1px solid #dbdbdb;
}

.panel-heading {
  padding: 0.5rem;
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  text-align: left;
}

.panel-heading-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.panel-heading-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  /* gap: 0.5rem; */
  min-width: 0; /* Allow container to shrink */
  flex: 1;
}

/* Make input shrink while maintaining minimum width */
.panel-heading-left :deep(.input) {
  min-width: 120px;
  width: 100%;
}

.scenarios-container {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
}
</style>
